import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import firebase from "firebase/app";
import "firebase/firestore";
import {
  Grid,
  Typography,
  Paper,
  Box,
  Chip,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
} from "@material-ui/core";

import {
  mensajeDocAObjeto,
  conversacionDocAObjeto,
  temasFrecuentes,
} from "./chatbotAnalyticsLogic";

const LIMITE_RECIENTES = 200;

function formatoFecha(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("es-MX");
}

// Solo para el equipo interno de Dashport (atleteAccount.perfil === 1) —
// mismo campo que ya usan ~20 lugares del código para acceso privilegiado
// (exportar listado global de eventos, configuración de negocio, etc.), no
// un mecanismo nuevo.
export default function ChatbotAnalyticsPage() {
  const atleteAccount = useSelector((state) => state.atlete.atleteAccount);
  const [tab, setTab] = useState(0);
  const [mensajes, setMensajes] = useState(null);
  const [sinResponder, setSinResponder] = useState(null);
  const [conversaciones, setConversaciones] = useState(null);
  const [chatbotEnabled, setChatbotEnabled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);

  const cargar = useCallback(() => {
    setLoading(true);
    setErrorCarga(null);
    const db = firebase.firestore();
    // Promise.allSettled (no Promise.all) a propósito: así cada lectura que
    // sí funciona se guarda igual, aunque otra falle — no tiene sentido
    // dejar todo en blanco (incluido el switch del flag) solo porque, por
    // ejemplo, el índice de una consulta todavía no termina de crearse.
    const consultas = [
      { nombre: "mensajes recientes", promesa: db.collection("chatMessages").orderBy("createdAt", "desc").limit(LIMITE_RECIENTES).get(), onOk: (snap) => setMensajes(snap.docs.map(mensajeDocAObjeto)) },
      { nombre: "mensajes sin responder", promesa: db.collection("chatMessages").where("wasAnswered", "==", false).orderBy("createdAt", "desc").limit(LIMITE_RECIENTES).get(), onOk: (snap) => setSinResponder(snap.docs.map(mensajeDocAObjeto)) },
      { nombre: "conversaciones", promesa: db.collection("chatConversations").orderBy("lastMessageAt", "desc").limit(LIMITE_RECIENTES).get(), onOk: (snap) => setConversaciones(snap.docs.map(conversacionDocAObjeto)) },
      { nombre: "appConfig/chatbot", promesa: db.collection("appConfig").doc("chatbot").get(), onOk: (snap) => setChatbotEnabled(snap.exists && snap.data().enabled === true) },
    ];

    Promise.allSettled(consultas.map((c) => c.promesa)).then((resultados) => {
      const fallos = [];
      resultados.forEach((r, i) => {
        if (r.status === "fulfilled") {
          consultas[i].onOk(r.value);
        } else {
          console.error(`Error cargando "${consultas[i].nombre}":`, r.reason);
          fallos.push(`${consultas[i].nombre}: [${r.reason.code || "?"}] ${r.reason.message || r.reason}`);
        }
      });
      setErrorCarga(fallos.length > 0 ? fallos.join(" | ") : null);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (atleteAccount && atleteAccount.perfil === 1) cargar();
  }, [atleteAccount, cargar]);

  if (!atleteAccount) return null;
  if (atleteAccount.perfil !== 1) {
    return (
      <Typography color="error">No tienes acceso a esta vista.</Typography>
    );
  }

  function toggleChatbot() {
    const nuevo = !chatbotEnabled;
    firebase
      .firestore()
      .collection("appConfig")
      .doc("chatbot")
      .set({ enabled: nuevo }, { merge: true })
      .then(() => setChatbotEnabled(nuevo));
  }

  function marcarResuelta(conversationId, resolved) {
    firebase
      .firestore()
      .collection("chatConversations")
      .doc(conversationId)
      .update({ resolved })
      .then(cargar);
  }

  const mensajesUsuario = (mensajes || []).filter((m) => m.role === "user");
  const temas = temasFrecuentes(mensajesUsuario);

  return (
    <Grid container direction="column">
      <Grid item>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h2" gutterBottom>
            Análisis del asistente
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!chatbotEnabled}
                  onChange={toggleChatbot}
                  disabled={chatbotEnabled === null}
                  color="primary"
                />
              }
              label="Asistente activo"
            />
            <Button onClick={cargar} disabled={loading}>
              {loading ? <CircularProgress size={18} /> : "Refrescar"}
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Últimas {LIMITE_RECIENTES} conversaciones/mensajes. Las preguntas que el asistente no
          supo responder son la lista de tareas de documentación.
        </Typography>
      </Grid>

      {errorCarga && (
        <Typography color="error" gutterBottom>
          {errorCarga}
        </Typography>
      )}

      {loading && !mensajes && !errorCarga ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid item>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} style={{ marginBottom: 16 }}>
            <Tab label={`Sin responder (${(sinResponder || []).length})`} />
            <Tab label="Preguntas recientes" />
            <Tab label="Conversaciones" />
            <Tab label="Temas más preguntados" />
          </Tabs>

          {tab === 0 && <TablaMensajes mensajes={sinResponder} vacio="No hay preguntas sin responder en el rango cargado." />}
          {tab === 1 && <TablaMensajes mensajes={mensajesUsuario} vacio="Todavía no hay preguntas." />}
          {tab === 2 && (
            <TablaConversaciones conversaciones={conversaciones} onMarcarResuelta={marcarResuelta} />
          )}
          {tab === 3 && (
            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
              {temas.length === 0 && (
                <Typography color="textSecondary">Sin datos suficientes todavía.</Typography>
              )}
              {temas.map((t) => (
                <Chip key={t.palabra} label={`${t.palabra} (${t.veces})`} />
              ))}
            </Box>
          )}
        </Grid>
      )}
    </Grid>
  );
}

function TablaMensajes({ mensajes, vacio }) {
  if (!mensajes || mensajes.length === 0) {
    return <Typography color="textSecondary">{vacio}</Typography>;
  }
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Fecha</TableCell>
            <TableCell>Mensaje</TableCell>
            <TableCell>Rol</TableCell>
            <TableCell>Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {mensajes.map((m) => (
            <TableRow key={m.id}>
              <TableCell style={{ whiteSpace: "nowrap" }}>{formatoFecha(m.createdAt)}</TableCell>
              <TableCell style={{ maxWidth: 480 }}>{m.content}</TableCell>
              <TableCell>{m.role}</TableCell>
              <TableCell>{m.error || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TablaConversaciones({ conversaciones, onMarcarResuelta }) {
  if (!conversaciones || conversaciones.length === 0) {
    return <Typography color="textSecondary">Todavía no hay conversaciones.</Typography>;
  }
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Inicio</TableCell>
            <TableCell>Último mensaje</TableCell>
            <TableCell>Usuario</TableCell>
            <TableCell>Evento</TableCell>
            <TableCell>Resuelta</TableCell>
            <TableCell align="right">Acción</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {conversaciones.map((c) => (
            <TableRow key={c.id}>
              <TableCell style={{ whiteSpace: "nowrap" }}>{formatoFecha(c.startedAt)}</TableCell>
              <TableCell style={{ whiteSpace: "nowrap" }}>{formatoFecha(c.lastMessageAt)}</TableCell>
              <TableCell>{c.userId}</TableCell>
              <TableCell>{c.eventId || "—"}</TableCell>
              <TableCell>
                {c.resolved === true ? "Sí" : c.resolved === false ? "No" : "—"}
              </TableCell>
              <TableCell align="right">
                <Button size="small" onClick={() => onMarcarResuelta(c.id, true)}>
                  Marcar resuelta
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
